import { Component, type ErrorInfo, type ReactNode, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";

// Lazy load all pages except Home for better initial load performance
const Auth = lazy(() => import("./pages/Auth"));
const About = lazy(() => import("./pages/About"));
const Classes = lazy(() => import("./pages/Classes"));
const Structure = lazy(() => import("./pages/Structure"));
const Contact = lazy(() => import("./pages/Contact"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PreEnrollment = lazy(() => import("./pages/PreEnrollment"));
const ChildRegistration = lazy(() => import("./pages/ChildRegistration"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const EmployeeRegistration = lazy(() => import("./pages/EmployeeRegistration"));
const Install = lazy(() => import("./pages/Install"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LGPD = lazy(() => import("./pages/LGPD"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));


const queryClient = new QueryClient();

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("ChunkLoadError")
  );
};

class ChunkLoadErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; isChunkError: boolean }> {
  state = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Route render error:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">
            {this.state.isChunkError ? "Atualização necessária" : "Não foi possível carregar a página"}
          </h1>
          <p className="text-muted-foreground">
            {this.state.isChunkError
              ? "O painel recebeu uma atualização. Recarregue para continuar com a versão mais recente."
              : "Recarregue a página. Se continuar acontecendo, tente novamente em alguns instantes."}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }
}

// Loading fallback component - defined outside to avoid recreation
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Wrapper to provide auth context for pages that need it
const ChildRegistrationWithAuth = () => (
  <AuthProvider>
    <ChildRegistration />
  </AuthProvider>
);

const AcceptInviteWithAuth = () => (
  <AuthProvider>
    <AcceptInvite />
  </AuthProvider>
);

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ChunkLoadErrorBoundary>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/redefinir-senha" element={<ResetPassword />} />
              <Route path="/sobre" element={<About />} />
              <Route path="/turmas" element={<Classes />} />
              <Route path="/estrutura" element={<Structure />} />
              <Route path="/contato" element={<Contact />} />
              <Route path="/planos" element={<Pricing />} />
              <Route path="/pre-matricula" element={<PreEnrollment />} />
              <Route path="/cadastro-pimpolho" element={<ChildRegistrationWithAuth />} />
              <Route path="/aceitar-convite" element={<AcceptInviteWithAuth />} />
              <Route path="/cadastro-funcionario" element={<EmployeeRegistration />} />
              <Route path="/instalar" element={<Install />} />
              <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
              <Route path="/lgpd" element={<LGPD />} />
              <Route path="/termos-uso" element={<TermsOfUse />} />
              <Route path="/aguardando-aprovacao" element={<PendingApproval />} />
              <Route path="/painel/*" element={<Dashboard />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ChunkLoadErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
